/**
 * Every preset except One game (screens 03b, 05): the window it covers, an
 * optional team filter, Custom dates when chosen, and the server's live
 * preview with a checkbox per game ("14 games match · 14 included").
 * Started games are shown and never included; TBD matchups show as
 * placeholders. The counts and the include state are the server's.
 */
import {
  Button,
  Checkbox,
  InlineBanner,
  Input,
  Select,
  Skeleton,
} from "@sportsmet/ui";
import type { ReactNode } from "react";

import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  SQUARES_CUSTOM_DATES_FROM,
  SQUARES_CUSTOM_DATES_TO,
  SQUARES_PREVIEW_INCLUDE_ALL,
  SQUARES_PREVIEW_LOADING,
  SQUARES_PREVIEW_NOT_SCHEDULED,
  SQUARES_PREVIEW_STARTED,
  SQUARES_PREVIEW_TBD,
  SQUARES_PREVIEW_ZERO_BLOCKED,
  SQUARES_TEAM_FILTER_ALL,
  SQUARES_TEAM_FILTER_LABEL,
  SQUARES_TEAM_REQUIRED_LABEL,
  squaresFirstLockLine,
  squaresPreviewCountLine,
  squaresPreviewExpectedMore,
} from "./squaresCopy";
import { SquaresConferencePicker } from "./SquaresConferencePicker";
import { squaresKickoffLabel, squaresMatchupLabel } from "./squaresModel";
import type {
  typeSquaresGamePreset,
  typeSquaresGameRule,
  typeSquaresPreviewData,
} from "./squaresTypes";
import type { typeSquaresTeam } from "./useSquaresLeagueTeams";

const ALL_TEAMS_VALUE = "all";

export type typeSquaresPresetPreviewProps = {
  /** The league the Conference picker lists conferences for. */
  leagueKey: string;
  preset: typeSquaresGamePreset;
  gameRule: typeSquaresGameRule;
  onChangeRule: (gameRule: typeSquaresGameRule) => void;
  onToggleGame: (eventId: string) => void;
  preview: typeSquaresPreviewData | null;
  isPreviewLoading: boolean;
  teams: readonly typeSquaresTeam[];
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
};

export const SquaresPresetPreview = (
  props: typeSquaresPresetPreviewProps,
): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const rule = props.gameRule;
  const isTeamRequired = rule.presetKey === "team";
  const isCustomDates = rule.presetKey === "customDates";
  const isConference = rule.presetKey === "conference";
  const preview = props.preview;
  const fromDay = rule.customDates?.fromDay ?? "";
  const toDay = rule.customDates?.toDay ?? "";

  return (
    <div className="flex flex-col gap-3" data-testid="squares-preset-preview">
      {preview === null ? null : (
        <p
          className="text-body-strong text-ink"
          data-testid="squares-preview-window"
        >
          {preview.window.label}
        </p>
      )}
      {isCustomDates ? (
        <div className="grid grid-cols-2 gap-3">
          <Input
            label={SQUARES_CUSTOM_DATES_FROM}
            type="date"
            value={fromDay}
            data-testid="squares-custom-from"
            onChange={(event) => {
              props.onChangeRule({
                ...rule,
                customDates: {
                  mode: "range",
                  fromDay: event.target.value,
                  toDay: toDay === "" ? event.target.value : toDay,
                },
              });
            }}
          />
          <Input
            label={SQUARES_CUSTOM_DATES_TO}
            type="date"
            value={toDay}
            min={fromDay === "" ? undefined : fromDay}
            data-testid="squares-custom-to"
            onChange={(event) => {
              props.onChangeRule({
                ...rule,
                customDates: {
                  mode: "range",
                  fromDay: fromDay === "" ? event.target.value : fromDay,
                  toDay: event.target.value,
                },
              });
            }}
          />
        </div>
      ) : null}
      {isConference ? (
        <SquaresConferencePicker
          leagueKey={props.leagueKey}
          conferenceKey={rule.conferenceKey ?? null}
          onChange={(conferenceKey) => {
            props.onChangeRule({
              ...rule,
              conferenceKey,
              excludedEventIds: [],
            });
          }}
        />
      ) : null}
      {props.preset.isTeamFilterable || isTeamRequired ? (
        <Select
          label={
            isTeamRequired
              ? SQUARES_TEAM_REQUIRED_LABEL
              : SQUARES_TEAM_FILTER_LABEL
          }
          value={rule.teamId ?? (isTeamRequired ? undefined : ALL_TEAMS_VALUE)}
          placeholder={SQUARES_TEAM_FILTER_ALL}
          items={[
            ...(isTeamRequired
              ? []
              : [{ value: ALL_TEAMS_VALUE, label: SQUARES_TEAM_FILTER_ALL }]),
            ...props.teams.map((team) => ({
              value: team.teamId,
              label: team.name,
            })),
          ]}
          onValueChange={(value) => {
            props.onChangeRule({
              ...rule,
              teamId: value === ALL_TEAMS_VALUE ? null : value,
              excludedEventIds: [],
            });
          }}
        />
      ) : null}
      {props.isPreviewLoading ? (
        <div
          className="flex flex-col gap-2"
          aria-label={SQUARES_PREVIEW_LOADING}
        >
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : preview === null ? null : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <p
              className="text-body text-ink"
              aria-live="polite"
              data-testid="squares-preview-count"
            >
              {squaresPreviewCountLine({
                matchCount: preview.matchCount,
                includedCount: preview.includedCount,
              })}
            </p>
            {rule.excludedEventIds.length > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                data-testid="squares-preview-include-all"
                onClick={() => {
                  props.onChangeRule({ ...rule, excludedEventIds: [] });
                }}
              >
                {SQUARES_PREVIEW_INCLUDE_ALL}
              </Button>
            ) : null}
          </div>
          {preview.firstLockAt === null ||
          preview.firstLockAt === undefined ? null : (
            <p className="text-caption text-ink-secondary">
              {squaresFirstLockLine(
                squaresKickoffLabel({
                  kickoffAt: preview.firstLockAt,
                  timeZone,
                }),
              )}
            </p>
          )}
          {!preview.window.isScheduled ? (
            <InlineBanner message={SQUARES_PREVIEW_NOT_SCHEDULED} />
          ) : null}
          {!preview.canCreate ? (
            <InlineBanner
              variant="warning"
              message={SQUARES_PREVIEW_ZERO_BLOCKED}
            />
          ) : null}
          {preview.games.length === 0 ? null : (
            <ul
              className="flex max-h-80 flex-col gap-1 overflow-y-auto rounded-md border border-hairline p-3"
              data-testid="squares-preview-list"
            >
              {preview.games.map((game) => {
                const matchup = game.isPlaceholder
                  ? SQUARES_PREVIEW_TBD
                  : squaresMatchupLabel({
                      awayTeamId: game.awayTeamId,
                      homeTeamId: game.homeTeamId,
                      teamsById: props.teamsById,
                    });
                const detail = [
                  squaresKickoffLabel({ kickoffAt: game.kickoffAt, timeZone }),
                  game.roundLabel ?? null,
                  game.isStarted ? SQUARES_PREVIEW_STARTED : null,
                ]
                  .filter(
                    (part): part is string => part !== null && part !== "",
                  )
                  .join(" · ");
                return (
                  <li key={game.eventId}>
                    <Checkbox
                      label={matchup}
                      description={detail}
                      checked={game.isIncluded}
                      disabled={game.isStarted}
                      onCheckedChange={() => {
                        props.onToggleGame(game.eventId);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          {preview.expectedMoreCount > 0 ? (
            <p className="text-caption text-ink-secondary">
              {squaresPreviewExpectedMore(preview.expectedMoreCount)}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};
